'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface StepOption {
  id: string;
  label: string;
  sublabel: string;
  price: number;
  specs: string[];
  img: string;
  alt: string;
  popular?: boolean;
}

interface BuilderStep {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  options: StepOption[];
}

const builderSteps: BuilderStep[] = [
{
  id: 'chemistry',
  title: 'Select Cell Chemistry',
  subtitle: 'Choose the lithium cell chemistry for your battery pack',
  icon: '🧬',
  options: [
  {
    id: 'lfp',
    label: 'LiFePO₄ (LFP)',
    sublabel: '3.2V nominal • Safest • Longest life',
    price: 0,
    specs: ['3.2V nominal', '2000–6000 cycles', 'Safest chemistry', 'Best for stationary storage'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bee22384-1767795971645.png",
    alt: 'LiFePO4 prismatic cells for safe long-life battery packs',
    popular: true
  },
  {
    id: 'nmc',
    label: 'NMC (Lithium Nickel Manganese Cobalt)',
    sublabel: '3.7V nominal • High energy density • Compact',
    price: 2000,
    specs: ['3.7V nominal', '500–1500 cycles', 'Higher energy density', 'Best for EVs & compact packs'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_19ee73f89-1778658398342.png",
    alt: 'NMC cylindrical 18650 cells for high energy density battery packs'
  },
  {
    id: 'lto',
    label: 'LTO (Lithium Titanate)',
    sublabel: '2.4V nominal • Ultra-fast charging • -40°C operation',
    price: 8000,
    specs: ['2.4V nominal', '10000+ cycles', 'Fastest charging', 'Best for extreme environments'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_170332455-1778658398300.png",
    alt: 'LTO titanate cells for extreme temperature and fast charging applications'
  }]

},
{
  id: 'capacity',
  title: 'Select Pack Capacity',
  subtitle: 'Choose the energy capacity for your application',
  icon: '⚡',
  options: [
  {
    id: '1kwh',
    label: '1 kWh Pack',
    sublabel: 'Small UPS • Home inverter • Basic backup',
    price: 8000,
    specs: ['~1 kWh usable', '12V / 24V / 48V', 'Up to 100Ah', 'Compact form factor'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c935442d-1769434753292.png",
    alt: '1kWh compact lithium battery pack for small UPS and home inverter'
  },
  {
    id: '5kwh',
    label: '5 kWh Pack',
    sublabel: 'Office backup • Small solar • Commercial UPS',
    price: 35000,
    specs: ['~5 kWh usable', '48V / 51.2V', 'Up to 100Ah @ 48V', 'Rack or cabinet mount'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ebe1e920-1778658398999.png",
    alt: '5kWh office backup lithium battery pack for commercial UPS systems',
    popular: true
  },
  {
    id: '10kwh',
    label: '10 kWh Pack',
    sublabel: 'Industrial backup • Solar storage • DG replacement',
    price: 65000,
    specs: ['~10 kWh usable', '48V / 96V', 'Up to 200Ah @ 48V', 'Floor cabinet / rack'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_166a12f9a-1777019207121.png",
    alt: '10kWh industrial lithium battery pack for solar storage and DG replacement'
  },
  {
    id: '30kwh',
    label: '30 kWh Pack',
    sublabel: 'Large commercial • Factory • BESS project',
    price: 180000,
    specs: ['~30 kWh usable', '96V / 153.6V', 'Modular design', 'Custom enclosure'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f0949ef3-1778658398693.png",
    alt: '30kWh large commercial BESS for factory and industrial applications'
  }]

},
{
  id: 'bms',
  title: 'Select BMS Type',
  subtitle: 'Choose the Battery Management System for your pack',
  icon: '🧠',
  options: [
  {
    id: 'basic',
    label: 'Standard BMS',
    sublabel: 'Passive balancing • RS485 • Basic protection',
    price: 2500,
    specs: ['Passive balancing (60mA)', 'RS485 communication', 'OVP, UVP, OCP, OTP', 'LED status indicators'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_15cbf4a93-1778658396970.png",
    alt: 'Standard BMS circuit board with passive balancing and RS485 communication'
  },
  {
    id: 'smart',
    label: 'Smart BMS',
    sublabel: 'Bluetooth • App monitoring • Active balancing',
    price: 5500,
    specs: ['Active balancing (1A)', 'Bluetooth + RS485', 'App monitoring', 'SOC/SOH display'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_17e893b37-1778658397980.png",
    alt: 'Smart BMS with Bluetooth connectivity and active cell balancing',
    popular: true
  },
  {
    id: 'industrial',
    label: 'Industrial BMS',
    sublabel: 'CAN Bus • Modbus • Active balancing • Cloud',
    price: 12000,
    specs: ['Active balancing (2A)', 'CAN + RS485 + Modbus', 'Cloud monitoring', 'SCADA compatible'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1011ba3c6-1778658398340.png",
    alt: 'Industrial BMS with CAN bus, Modbus, and cloud monitoring capabilities'
  }]

},
{
  id: 'enclosure',
  title: 'Select Enclosure Type',
  subtitle: 'Choose the housing and mounting style for your pack',
  icon: '📦',
  options: [
  {
    id: 'wall',
    label: 'Wall-Mount Enclosure',
    sublabel: 'Compact • IP54 • Indoor use',
    price: 1500,
    specs: ['IP54 rated', 'Wall-mount brackets', 'Powder-coated steel', 'Ventilated design'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_10591f312-1778658398121.png",
    alt: 'Wall-mounted battery enclosure with IP54 rating for indoor installation'
  },
  {
    id: 'rack',
    label: 'Rack-Mount Enclosure',
    sublabel: '19\" rack • IP55 • Data center ready',
    price: 3000,
    specs: ['19" rack compatible', 'IP55 rated', '2U / 4U options', 'Front access'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfcd4824-1765360090326.png",
    alt: '19-inch rack-mount battery enclosure for data center and server room use',
    popular: true
  },
  {
    id: 'floor',
    label: 'Floor-Standing Cabinet',
    sublabel: 'Large capacity • IP55 • Industrial grade',
    price: 8000,
    specs: ['IP55 rated', 'Lockable cabinet', 'Forklift pockets', 'Modular expansion'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1929b99d7-1778658400098.png",
    alt: 'Floor-standing industrial battery cabinet with IP55 rating and forklift pockets'
  }]

},
{
  id: 'warranty',
  title: 'Select Warranty Plan',
  subtitle: 'Choose the warranty and support package for your pack',
  icon: '🛡️',
  options: [
  {
    id: '2yr',
    label: '2-Year Standard Warranty',
    sublabel: 'Parts & labor • Email support',
    price: 0,
    specs: ['2 years parts & labor', 'Email support', 'Replacement cells', 'Standard SLA'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_15e7e5cdc-1778658397969.png",
    alt: 'Standard 2-year warranty certificate for lithium battery pack'
  },
  {
    id: '5yr',
    label: '5-Year Extended Warranty',
    sublabel: 'Parts & labor • Phone support • On-site',
    price: 4000,
    specs: ['5 years parts & labor', 'Phone + email support', 'On-site service', 'Priority SLA'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bee22384-1767795971645.png",
    alt: 'Extended 5-year warranty with on-site service for industrial battery systems',
    popular: true
  },
  {
    id: '7yr',
    label: '7-Year Premium Warranty',
    sublabel: 'Full coverage • 24\/7 support • Replacement guarantee',
    price: 9000,
    specs: ['7 years full coverage', '24/7 support hotline', 'Pack replacement guarantee', 'Annual health check'],
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1139e3179-1778658397990.png",
    alt: 'Premium 7-year warranty with 24/7 support and annual health check service'
  }]

}];


export default function BatteriesPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<Record<string, StepOption | null>>({});
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('visible');});},
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSelect = (stepId: string, option: StepOption) => {
    setSelections((prev) => ({ ...prev, [stepId]: option }));
  };

  const handleNext = () => {
    if (currentStep < builderSteps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handleBack = () => {
    if (showSummary) {
      setShowSummary(false);
    } else if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setSelections({});
    setShowSummary(false);
  };

  const getTotalPrice = () => {
    return Object.values(selections).reduce((sum, opt) => sum + (opt?.price ?? 0), 0);
  };

  const step = builderSteps[currentStep];
  const currentSelection = selections[step?.id];
  const progress = (currentStep + 1) / builderSteps.length * 100;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 circuit-bg opacity-30" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 70%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="section-label mb-6">Batteries</div>
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            Custom Lithium
            <br />
            <span className="gradient-text-green">Battery Packs</span>
          </h1>
          <p className="text-slate-600 text-lg max-w-3xl leading-relaxed">
            Design and manufacture precision lithium battery packs for any application.
            Use our interactive Battery Pack Builder to configure your pack and get instant pricing.
          </p>
        </div>
      </section>

      {/* Cell Types Gallery */}
      <section className="py-12 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 animate-on-scroll">
            <h2 className="text-2xl font-bold text-slate-900">Lithium Cell Portfolio</h2>
            <p className="text-slate-600 text-sm mt-1">We work with all major cell formats and chemistries</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_177d7917e-1767728457986.png", alt: 'Cylindrical 18650 lithium cells in rows showing cell format', label: '18650 Cylindrical', sub: 'NMC / LFP' },
            { img: 'https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?w=400&q=80', alt: 'Large format prismatic LFP cells for industrial battery packs', label: 'Prismatic LFP', sub: 'LiFePO₄' },
            { img: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=400&q=80', alt: '21700 cylindrical cells for high energy density applications', label: '21700 Cylindrical', sub: 'NMC High-Energy' },
            { img: 'https://images.unsplash.com/photo-1620714223084-8fcacc2dbe4d?w=400&q=80', alt: 'Pouch lithium polymer cells for flexible custom battery designs', label: 'Pouch Cells', sub: 'LiPo / NMC' }].
            map((cell, i) =>
            <div key={i} className="card-dark overflow-hidden group animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="aspect-video overflow-hidden">
                  <img src={cell.img} alt={cell.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <div className="text-slate-900 font-semibold text-sm">{cell.label}</div>
                  <div className="text-primary text-xs mt-0.5">{cell.sub}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Battery Pack Builder */}
      <section className="py-16 calculator-light" id="builder">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Interactive Tool</div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Design Your <span className="gradient-text-green">Battery Pack</span>
            </h2>
            <p className="text-slate-600">
              Select your requirements step by step and get a complete specification and price estimate.
            </p>
          </div>

          <div className="card-dark p-8">
            {!showSummary ?
            <>
                {/* Progress */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-500 text-sm">Step {currentStep + 1} of {builderSteps.length}</span>
                    <span className="text-primary text-sm font-semibold">{Math.round(progress)}% complete</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* Step dots */}
                <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
                  {builderSteps.map((s, i) =>
                <React.Fragment key={i}>
                      <div
                    className={`step-dot flex-shrink-0 ${
                    i < currentStep ? 'completed' : i === currentStep ? 'active' : 'pending'}`
                    }
                    title={s.title}>
                    
                        {i < currentStep ?
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                           </svg> :

                    <span className="text-xs">{s.icon}</span>
                    }
                      </div>
                      {i < builderSteps.length - 1 &&
                   <div className={`step-line ${i < currentStep ? 'completed' : ''}`} />
                  }
                    </React.Fragment>
                )}
                </div>

                {/* Step header */}
                <div className="mb-6">
                  <h3 className="text-slate-900 font-bold text-xl mb-1">
                    {step.icon} {step.title}
                  </h3>
                  <p className="text-slate-500 text-sm">{step.subtitle}</p>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {step.options.map((opt) =>
                <button
                  key={opt.id}
                  onClick={() => handleSelect(step.id, opt)}
                  className={`selection-card text-left relative ${
                  currentSelection?.id === opt.id ? 'selected' : ''}`
                  }>
                  
                      {opt.popular &&
                  <div className="absolute -top-2 -right-2">
                          <span className="badge badge-green text-[10px]">Popular</span>
                        </div>
                  }
                      <img
                    src={opt.img}
                    alt={opt.alt}
                    className="w-full h-28 object-cover rounded-lg mb-3" />
                  
                      <div className="font-semibold text-slate-900 text-sm mb-1">{opt.label}</div>
                      <div className="text-slate-500 text-xs mb-3">{opt.sublabel}</div>
                      <ul className="space-y-1 mb-3">
                        {opt.specs.map((spec) =>
                    <li key={spec} className="flex items-center gap-1.5 text-xs text-slate-600">
                             <span className="w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                             {spec}
                           </li>
                    )}
                      </ul>
                      <div className="text-primary font-semibold text-sm">
                        {opt.price === 0 ? 'Included' : `+${formatPrice(opt.price)}`}
                      </div>
                      {currentSelection?.id === opt.id &&
                  <div className="absolute top-3 right-3">
                          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                            <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                  }
                    </button>
                )}
                </div>

                {/* Running total */}
                {Object.keys(selections).length > 0 &&
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 flex items-center justify-between">
                    <span className="text-slate-600 text-sm">Running Total:</span>
                    <span className="text-primary font-bold text-lg">{formatPrice(getTotalPrice())}</span>
                  </div>
              }

                {/* Navigation */}
                <div className="flex items-center justify-between">
                  <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="btn-secondary disabled:opacity-30 disabled:cursor-not-allowed">
                  
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                    </svg>
                    Back
                  </button>
                  <button
                  onClick={handleNext}
                  disabled={!currentSelection}
                  className="btn-primary disabled:opacity-30 disabled:cursor-not-allowed">
                  
                    {currentStep === builderSteps.length - 1 ? 'View Full Specification' : 'Next Step'}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </> :

            /* Summary */
            <div>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-slate-900 font-bold text-2xl mb-2">Your Custom Battery Pack</h3>
                  <p className="text-slate-600 text-sm">Complete specification based on your selections</p>
                </div>

                {/* Selections summary */}
                <div className="space-y-3 mb-8">
                  {builderSteps.map((s) => {
                  const sel = selections[s.id];
                  if (!sel) return null;
                  return (
                    <div key={s.id} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <span className="text-2xl">{s.icon}</span>
                        <div className="flex-1">
                          <div className="text-slate-500 text-xs mb-0.5">{s.title}</div>
                          <div className="text-slate-900 font-semibold text-sm">{sel.label}</div>
                          <div className="text-slate-600 text-xs">{sel.sublabel}</div>
                        </div>
                        <div className="text-primary font-semibold text-sm flex-shrink-0">
                          {sel.price === 0 ? 'Included' : `+${formatPrice(sel.price)}`}
                        </div>
                      </div>);

                })}
                </div>

                {/* Specs */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-6">
                  <h4 className="text-slate-900 font-bold mb-4 flex items-center gap-2">
                    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Full Pack Specifications
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    {[
                  { label: 'Cell Chemistry', value: selections.chemistry?.label ?? '—' },
                  { label: 'Pack Capacity', value: selections.capacity?.label ?? '—' },
                  { label: 'BMS Type', value: selections.bms?.label ?? '—' },
                  { label: 'Enclosure', value: selections.enclosure?.label ?? '—' },
                  { label: 'Warranty', value: selections.warranty?.label ?? '—' },
                  { label: 'Cycle Life', value: selections.chemistry?.id === 'lto' ? '10000+ cycles' : selections.chemistry?.id === 'nmc' ? '500–1500 cycles' : '2000–6000 cycles' },
                  { label: 'BMS Protection', value: 'OVP, UVP, OCP, OTP, SCP' },
                  { label: 'Delivery', value: '3–4 weeks (custom)' },
                  { label: 'Customization', value: 'Available on request' },
                  { label: 'Certifications', value: 'IEC 62133, UN 38.3' }].
                  map((item) =>
                  <div key={item.label} className="spec-row">
                        <span className="text-slate-500 text-sm">{item.label}</span>
                        <span className="text-slate-900 text-sm font-medium">{item.value}</span>
                      </div>
                  )}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-primary/10 border border-primary/20 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-slate-600 text-sm">Estimated Total (Base Price)</div>
                      <div className="text-xs text-slate-500 mt-1">Final price may vary based on quantity and customization</div>
                    </div>
                    <div className="text-primary font-bold text-3xl">{formatPrice(getTotalPrice())}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 justify-center">
                  <Link href="/contact" className="btn-primary text-base px-8 py-3">
                    Request Final Quote
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <a
                  href={`https://wa.me/919737701071?text=Hi%2C%20I%20designed%20a%20custom%20battery%20pack%20on%20your%20website.%20Chemistry%3A%20${encodeURIComponent(selections.chemistry?.label ?? '')}%2C%20Capacity%3A%20${encodeURIComponent(selections.capacity?.label ?? '')}%2C%20BMS%3A%20${encodeURIComponent(selections.bms?.label ?? '')}.%20Please%20send%20me%20a%20quote.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-base px-8 py-3">
                  
                    Share via WhatsApp
                  </a>
                  <button onClick={handleReset} className="btn-outline">
                    Design Another Pack
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </section>

      {/* Battery images */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 animate-on-scroll">
            <h2 className="text-2xl font-bold text-slate-900">Our Battery Pack Gallery</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_13396ba4c-1764710167910.png", alt: 'Custom 18650 cylindrical cell battery pack assembly', label: '18650 Pack' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_17b7fb227-1767795973231.png", alt: 'Prismatic LFP cell industrial battery pack', label: 'LFP Industrial' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3908970-1767987580103.png", alt: 'High voltage NMC battery pack for EV applications', label: 'NMC EV Pack' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_12e26bfb9-1766810847273.png", alt: 'BMS integrated battery pack with protection electronics', label: 'BMS Integrated' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_195fd9f41-1772680347249.png", alt: 'Solar storage battery pack with MPPT controller', label: 'Solar Storage' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_160996231-1778658399622.png", alt: 'QC testing of lithium battery packs in manufacturing', label: 'QC Testing' }].
            map((item, i) =>
            <div key={i} className="relative group overflow-hidden rounded-2xl border border-slate-200 animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <img src={item.img} alt={item.alt} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <span className="badge badge-green">{item.label}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}